import React, { useEffect, useState } from 'react';
// import './index.css';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';

const CenterDashboard = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [admissions, setAdmissions] = useState([]);
  const [fees, setFees] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [counselorFilter, setCounselorFilter] = useState('all');
  const center = localStorage.getItem('center');
  const api = process.env.REACT_APP_API;

  // Fetch all data
  useEffect(() => {
    const token = localStorage.getItem('jwt_token');
    if (!token) {
      setError(new Error('User not authenticated'));
      setLoading(false);
      return;
    }
    const fetchAll = async () => {
      try {
        // Enquiries
        const enquiriesRes = await axios.get(`${api}/enquiries`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Admissions
        const admissionsRes = await axios.get(`${api}/api/admissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Fees
        const feesRes = await axios.get(`${api}/fees`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Employees (Counselors)
        const employeesRes = await axios.get(`${api}/employees`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEnquiries(enquiriesRes.data.filter(e => e.centerName === center));
        setAdmissions(admissionsRes.data.filter(a => a.centerName === center));
        setFees(feesRes.data.filter(f => f.center === center));
        setEmployees(employeesRes.data.employees.filter(emp => emp.center === center && emp.role === 'Councillor'));
        setLoading(false);
      } catch (err) {
        setError(err);
        setLoading(false);
      }
    };
    fetchAll();
  }, [api, center]);

  // Date range filter logic
  const isWithinRange = (dateStr) => {
    if (!fromDate && !toDate) return true;
    const date = new Date(dateStr);
    if (fromDate && date < new Date(fromDate)) return false;
    if (toDate && date > new Date(toDate)) return false;
    return true;
  };

  // Counselor filter logic
  const filterByCounselor = (arr, key = 'counselorName') =>
    counselorFilter === 'all' ? arr : arr.filter(e => (e[key] || '').toLowerCase() === counselorFilter);

  // Filtered admissions and enquiries by date range and counselor
  const filteredAdmissions = filterByCounselor(admissions, 'counselorName').filter(a => isWithinRange(a.createdAt));
  const filteredEnquiries = filterByCounselor(enquiries, 'counselorName').filter(e => isWithinRange(e.date));

  // For fees, match by IdNo from filteredAdmissions (since fees may not have counselorName)
  const filteredIdNos = filteredAdmissions.map(a => a.IdNo);
  const filteredFees = fees.filter(f => filteredIdNos.includes(f.IdNo));

  // Stats
  const totalCenterFees = filteredAdmissions.reduce((sum, a) => sum + (parseFloat(a.totalFees) || 0), 0);
  const totalCollection = filteredFees.reduce((sum, f) => {
    if (!f.terms) return sum;
    return sum + f.terms.reduce((tSum, t) => tSum + (parseFloat(t.amountPaid) || 0), 0);
  }, 0);
  const pendingCollection = totalCenterFees - totalCollection;

  const totalEnquiries = filteredEnquiries.length;
  const joinedEnquiries = filteredEnquiries.filter(e => e.status === 'joined').length;
  const notJoinedEnquiries = totalEnquiries - joinedEnquiries;

  // Prepare monthly data for bar chart (overall)
  const pad = n => n.toString().padStart(2, '0');
  const monthlyStats = {};
  filteredAdmissions.forEach(a => {
    const date = new Date(a.createdAt);
    const key = `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
    if (!monthlyStats[key]) monthlyStats[key] = { month: key, admissions: 0, collection: 0 };
    monthlyStats[key].admissions += 1;
  });
  filteredFees.forEach(f => {
    if (!f.terms) return;
    f.terms.forEach(term => {
      const date = new Date(term.datePaid);
      const key = `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
      if (!monthlyStats[key]) monthlyStats[key] = { month: key, admissions: 0, collection: 0 };
      monthlyStats[key].collection += parseFloat(term.amountPaid) || 0;
    });
  });
  const monthlyData = Object.values(monthlyStats).sort((a, b) => a.month.localeCompare(b.month));

  // Prepare grouped bar chart data for monthly collection by counselor
  const counselorNames = employees.map(e => e.username.toLowerCase());
  const monthlyCounselorStats = {};
  filteredAdmissions.forEach(a => {
    const date = new Date(a.createdAt);
    const monthKey = `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
    const counselor = (a.counselorName || '').toLowerCase();
    if (!monthlyCounselorStats[monthKey]) monthlyCounselorStats[monthKey] = { month: monthKey };
    counselorNames.forEach(name => {
      if (!(name in monthlyCounselorStats[monthKey])) monthlyCounselorStats[monthKey][name] = 0;
    });
  });
  filteredFees.forEach(f => {
    const admission = filteredAdmissions.find(a => a.IdNo === f.IdNo);
    if (!admission) return;
    const counselor = (admission.counselorName || '').toLowerCase();
    if (!f.terms) return;
    f.terms.forEach(term => {
      const date = new Date(term.datePaid);
      const monthKey = `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
      if (!monthlyCounselorStats[monthKey]) monthlyCounselorStats[monthKey] = { month: monthKey };
      counselorNames.forEach(name => {
        if (!(name in monthlyCounselorStats[monthKey])) monthlyCounselorStats[monthKey][name] = 0;
      });
      if (counselor) monthlyCounselorStats[monthKey][counselor] += parseFloat(term.amountPaid) || 0;
    });
  });
  const monthlyCounselorData = Object.values(monthlyCounselorStats).sort((a, b) => a.month.localeCompare(b.month));
  const counselorColors = ["#8884d8", "#82ca9d", "#ffc658", "#ff7f50", "#8dd1e1", "#a4de6c", "#d0ed57", "#d8854f", "#b47ddb", "#e28743"];

  return (
    <div className="dashboard-container">
      <h2 className="dashboard-title">Center Dashboard</h2>
      <div className="dashboard-filters">
        <label>Counselor: </label>
        <select value={counselorFilter} onChange={e => setCounselorFilter(e.target.value)}>
          <option value="all">All</option>
          {employees.map(e => (
            <option value={e.username.toLowerCase()} key={e._id}>{e.username}</option>
          ))}
        </select>
        <label>From: </label>
        <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
        <label>To: </label>
        <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
      </div>
      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div className="dashboard-error">Error: {error.message}</div>
      ) : (
        <>
          <div className="dashboard-stats">
            <div className="dashboard-card">Total Center Fees: ₹ {totalCenterFees.toLocaleString()}</div>
            <div className="dashboard-card">Total Center Collection: ₹ {totalCollection.toLocaleString()}</div>
            <div className="dashboard-card">Pending Collection: ₹ {pendingCollection.toLocaleString()}</div>
            <div className="dashboard-card">Total Enquiries: {totalEnquiries}</div>
            <div className="dashboard-card">Joined Enquiries: {joinedEnquiries}</div>
            <div className="dashboard-card">Not Joined Enquiries: {notJoinedEnquiries}</div>
          </div>
          <h3 style={{marginTop: 40}}>Monthly Admissions & Collection</h3>
          <div style={{ width: '100%', height: 350, background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px #eee', padding: 24 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="admissions" fill="#8884d8" name="Admissions" radius={[8, 8, 0, 0]} />
                <Bar dataKey="collection" fill="#82ca9d" name="Collection" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <h3 style={{marginTop: 40}}>Monthly Collection by Counselor</h3>
          <div style={{ width: '100%', height: 350, background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px #eee', padding: 24 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyCounselorData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                {counselorNames.map((name, idx) => (
                  <Bar key={name} dataKey={name} fill={counselorColors[idx % counselorColors.length]} name={name} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

export default CenterDashboard; 